import { useHydrate, useRefs } from '~/framework'
import { fetchPLP } from '~/utils'
import productCart from './product-card-overlay'
import productForm from './product-form'
import expander from './expander'
import imageFadeIn from './image-fade-in'
import { Component } from '@/types'

const PAGE_SIZE = 12

const component: Component = ref => {
  if (!ref.infiniteLoader) return

  const loadingStates = new WeakMap()

  function callback(entries, observer) {
    entries.forEach(async entry => {
      if (!entry.isIntersecting) return

      const isLoading = loadingStates.get(entry.target)

      if (isLoading) return
      if (entry.target.classList.contains('done')) return

      loadingStates.set(entry.target, true)

      try {
        const searchParams = new URLSearchParams(window.location.search)

        if (searchParams.get('layout') === '3') return

        const target = ref.plp.find(
          element =>
            element.dataset.collection ===
            entry.target.dataset.infiniteLoadHandle
        )

        if (!target) return

        // Safeguard: If we have existing products and initial page is 1, skip to page 2
        // This handles cases where combined-filter already loaded page 1
        const existingProductIds = Array.from(target.querySelectorAll('[data-product-id]'))
          .map(el => el.getAttribute('data-product-id'))

        let currentPage = entry.target.dataset.page === undefined
          ? entry.target.dataset.initialPage || '2'
          : parseInt(entry.target.dataset.page) + 1

        if (entry.target.dataset.initialPage === '1' && existingProductIds.length > 0 && entry.target.dataset.page === undefined) {
          currentPage = 2
          entry.target.dataset.page = '1' // Mark that we've "loaded" page 1
        }

        // Load content while the loader is still close to viewport (handles footer issue)
        // But limit iterations to prevent breaking horizontal scroll
        let iterations = 0
        const maxIterations = 3

        // Always load at least one page when intersecting, then continue if still close to viewport
        do {
          searchParams.set('page', currentPage.toString())
          searchParams.set('limit', PAGE_SIZE.toString())

          const fetchUrl = entry.target.href + '?' + searchParams.toString()

          const { plp } = await fetchPLP(fetchUrl)

          if (!plp) break

          const allItems = Array.from(plp.querySelectorAll('li'))
          // The pagination marker is a hidden <li> emitted by Liquid only
          // when paginate.next exists. It carries data-has-next-page even on
          // pages that filtered all visible products out, so we trust it as
          // the authoritative "more pages" signal.
          const hasPaginationMarker = allItems.some(item =>
            item.matches('[data-pagination-marker]')
          )
          // Only treat product <li>s as appendable. Pages past the last one
          // (e.g. page 2 of a single-product lazy sub-collection that
          // combined-filter already loaded) render a "no results" <li> with
          // just a heading — appending it would surface "Ingen resultater"
          // inside the carousel.
          const realItems = allItems.filter(
            item =>
              !item.matches('[data-pagination-marker]') &&
              item.querySelector('.product-card')
          )

          entry.target.dataset.page = currentPage.toString()

          if (realItems.length > 0) {
            realItems.forEach(item => {
              const productLink = item.querySelector('a[href*="/products/"]')
              if (productLink) {
                const productUrl = productLink.getAttribute('href')
                const productId = productUrl?.split('/products/')[1]?.split('?')[0]
                if (productId) {
                  item.setAttribute('data-product-id', productId)
                }
              }
              target.append(item)
            })

            useHydrate([productCart, expander, imageFadeIn, productForm]).hydrate(
              useRefs({ root: target })
            )
          }

          // Backwards-compat: if the marker isn't there (older cached pages
          // or other contexts), fall back to the previous heuristic on the
          // first real item.
          const hasNextPage =
            hasPaginationMarker ||
            realItems[0]?.dataset.hasNextPage !== undefined

          if (!hasNextPage) {
            entry.target.classList.add('done')
            observer.unobserve(entry.target)
            break
          }

          currentPage++
          iterations++

          // Small delay to prevent overwhelming the browser
          await new Promise(resolve => setTimeout(resolve, 50))

          // Extra check: if loader is within 750px of viewport and we haven't hit max iterations, continue loading
        } while (
          (entry.target.getBoundingClientRect().top < window.innerHeight + 750 && iterations < maxIterations) ||
          (iterations === 1 && entry.target.getBoundingClientRect().top < window.innerHeight + 200)
        )
      } catch (error) {
        // Silently handle errors
      } finally {
        loadingStates.set(entry.target, false)
      }
    })
  }

  const observer = new IntersectionObserver(callback, { rootMargin: '750px' })

  ref.infiniteLoader.forEach(element => {
    observer.observe(element)
  })

  function resetInfiniteLoaders() {
    ref.infiniteLoader.forEach(element => {
      element.removeAttribute('data-page')
      element.classList.remove('done')
      loadingStates.set(element, false)
      observer.observe(element)
    })
  }

  window.addEventListener('se-event.filter.updated', resetInfiniteLoaders)

  return () => {
    observer.disconnect()
  }
}

export default component
